import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Artists() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", label_id: "", bio: "" });
  const [inviteFor, setInviteFor] = useState(null);
  const [inv, setInv] = useState({ login_email: "", login_password: "Admin@123" });
  const canCreate = ["super_admin", "label_manager", "label"].includes(user.role);
  const canInvite = ["super_admin", "label_manager", "label"].includes(user.role);

  const load = () => api.get("/artists").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/labels").then((r) => setLabels(r.data)); }, []);
  const [q, setQ] = useState("");

  const create = async () => {
    if (!f.name) return toast.error("Name required");
    await api.post("/artists", { ...f, label_id: f.label_id || null });
    toast.success("Artist created");
    setOpen(false); setF({ name: "", label_id: "", bio: "" }); load();
  };

  const invite = async () => {
    if (!inv.login_email) return toast.error("Login email required");
    try {
      await api.post("/artists/request-access", { artist_id: inviteFor.id, ...inv });
      toast.success("Login invite sent — pending Super Admin approval");
      setInviteFor(null); setInv({ login_email: "", login_password: "Admin@123" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const labelName = (id) => labels.find((l) => l.id === id)?.name || "—";
  const filtered = rows.filter((a) => `${a.name} ${labelName(a.label_id)} ${a.bio || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="artists-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Artists</h1><p className="mt-1 text-muted-foreground">Manage your artist roster.</p></div>
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="new-artist-btn"><Plus className="mr-2 h-4 w-4" />Create Artist</Button>}
      </div>
      <Card className="p-4"><Input placeholder="Search artists by name, label or bio…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="artist-search" /></Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Label</TableHead><TableHead>Bio</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">No artists.</TableCell></TableRow>}
            {filtered.map((a) => (
              <TableRow key={a.id} data-testid={`artist-row-${a.id}`}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell>{labelName(a.label_id)}</TableCell>
                <TableCell className="text-muted-foreground">{a.bio || "—"}</TableCell>
                <TableCell className="text-right">
                  {canInvite && <Button size="sm" variant="secondary" onClick={() => setInviteFor(a)} data-testid={`invite-artist-${a.id}`}><UserPlus className="mr-2 h-4 w-4" />Invite Login</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Artist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="artist-name" className="mt-1" /></div>
            <div><Label>Label</Label>
              <Select value={f.label_id} onValueChange={(v) => setF({ ...f, label_id: v })}>
                <SelectTrigger className="mt-1" data-testid="artist-label"><SelectValue placeholder="Select label" /></SelectTrigger>
                <SelectContent>{labels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bio</Label><Input value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="artist-bio" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-artist-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteFor} onOpenChange={(o) => !o && setInviteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite {inviteFor?.name} to their own login</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Creates an artist login account. Access activates after Super Admin approval.</p>
          <div className="space-y-3">
            <div><Label>Login Email</Label><Input value={inv.login_email} onChange={(e) => setInv({ ...inv, login_email: e.target.value })} data-testid="invite-email" className="mt-1" /></div>
            <div><Label>Temporary Password</Label><Input value={inv.login_password} onChange={(e) => setInv({ ...inv, login_password: e.target.value })} data-testid="invite-password" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setInviteFor(null)}>Cancel</Button>
            <Button onClick={invite} data-testid="send-invite-btn">Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
